import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { useTranslation } from "react-i18next";

export default function Footer() {
    const { t } = useTranslation();
    return (
        <footer className="border-t border-neutral-200 mt-16 sm:mt-24 lg:mt-32 bg-white" data-testid="site-footer">
            {/* Research-use-only disclaimer band */}
            <div className="border-y border-amber-500/30 bg-amber-50" data-testid="research-disclaimer">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-5 sm:py-6 flex flex-col md:flex-row gap-3 sm:gap-4 items-start md:items-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5 md:mt-0" />
                    <div className="text-xs md:text-sm text-amber-900/90 leading-relaxed">
                        <span className="font-bold uppercase tracking-[0.18em] text-amber-700 mr-2 block sm:inline mb-1 sm:mb-0">
                            {t("footer.disclaimerTitle")}
                        </span>
                        {t("footer.disclaimer")}{" "}
                        <Link
                            to="/terms"
                            className="underline underline-offset-2 hover:text-amber-700"
                            data-testid="footer-disclaimer-link"
                        >
                            {t("footer.readFullDisclaimer")}
                        </Link>
                    </div>
                </div>
            </div>

            <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 py-12 sm:py-16 grid grid-cols-2 md:grid-cols-4 gap-8 sm:gap-12">
                <div className="col-span-2 md:col-span-2">
                    <div className="flex items-center gap-3 mb-5">
                        <div className="relative w-7 h-7">
                            <div className="absolute inset-0 rounded-lg border border-[#00aeef]" />
                            <div className="absolute inset-1.5 rounded-lg bg-[#00aeef]/25" />
                        </div>
                        <div className="font-display font-extrabold uppercase tracking-[0.18em] text-sm">
                            Nexus<span className="text-[#0077b6]">.</span>Peptides
                        </div>
                    </div>
                    <p className="text-sm text-neutral-500 max-w-md leading-relaxed" data-testid="footer-tagline">{t("footer.tagline")}</p>
                    <div className="mt-6 inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] text-[#0077b6] border border-[#00aeef]/30 px-3 py-1.5 rounded-lg">
                        <span className="w-1.5 h-1.5 bg-[#0089c8] rounded-full nx-pulse" />
                        {t("footer.purityBadge")}
                    </div>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] mb-4">
                        {t("footer.company")}
                    </div>
                    <ul className="space-y-2.5 text-sm text-neutral-600">
                        <li><Link to="/about" className="hover:text-neutral-900">{t("footer.about")}</Link></li>
                        <li><Link to="/research" className="hover:text-neutral-900">{t("footer.research")}</Link></li>
                        <li><Link to="/bulk-quote" className="hover:text-neutral-900" data-testid="footer-link-bulk">{t("footer.bulkQuote")}</Link></li>
                        <li><Link to="/faq" className="hover:text-neutral-900">{t("footer.faq")}</Link></li>
                        <li><Link to="/shop" className="hover:text-neutral-900">{t("footer.shop")}</Link></li>
                        <li><Link to="/track-order" className="hover:text-neutral-900" data-testid="footer-link-track-order">{t("footer.trackOrder")}</Link></li>
        <li><Link to="/contact" className="hover:text-neutral-900">{t("footer.contact")}</Link></li>
                    </ul>
                </div>

                <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-[#0077b6] mb-4">
                        {t("footer.legal")}
                    </div>
                    <ul className="space-y-2.5 text-sm text-neutral-600">
                        <li>
                            <Link to="/privacy" className="hover:text-neutral-900" data-testid="footer-link-privacy">
                                {t("footer.privacy")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/gdpr" className="hover:text-neutral-900" data-testid="footer-link-gdpr">
                                {t("footer.gdpr")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/cookies" className="hover:text-neutral-900" data-testid="footer-link-cookies">
                                {t("footer.cookies")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/terms" className="hover:text-neutral-900" data-testid="footer-link-terms">
                                {t("footer.terms")}
                            </Link>
                        </li>
                        <li>
                            <Link to="/complaints" className="hover:text-neutral-900" data-testid="footer-link-complaints">
                                {t("footer.complaints")}
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-neutral-200 py-5 sm:py-6">
                <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-12 flex flex-col md:flex-row items-center justify-between gap-3 text-[10px] sm:text-xs text-neutral-500 text-center md:text-left">
                    <div>{t("footer.copyright", { year: new Date().getFullYear() })}</div>
                    <div className="font-mono">{t("footer.status")}</div>
                </div>
            </div>
        </footer>
    );
}
